import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Theme /*, ThemePanel */ } from '@radix-ui/themes';

type ThemePhase = 'morning' | 'noon' | 'evening' | 'midnight';

const ThemeContext = createContext<{ phase: ThemePhase } | undefined>(
  undefined
);

const calculatePhase = (): ThemePhase => {
  const hour = new Date().getHours();
  if (hour >= 3 && hour < 9) return 'morning';
  if (hour >= 9 && hour < 15) return 'noon';
  if (hour >= 15 && hour < 21) return 'evening';
  return 'midnight';
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<ThemePhase>(calculatePhase);

  useEffect(() => {
    const applyTheme = () => {
      const nextPhase = calculatePhase();
      setPhase(nextPhase);
      document.documentElement.setAttribute('data-theme', nextPhase);
    };

    applyTheme();
    const interval = window.setInterval(applyTheme, 60 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, []);

  const value = useMemo(() => ({ phase }), [phase]);

  return (
    <ThemeContext value={value}>
      <Theme
        accentColor="grass"
        grayColor="sand"
        radius="large"
        scaling="100%"
        panelBackground="translucent"
      >
        {children}
        {/* <ThemePanel /> */}
      </Theme>
    </ThemeContext>
  );
}

export const useThemePhase = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemePhase must be used within ThemeProvider');
  }
  return context.phase;
};
